// ─────────────────────────────────────────────────────────────────────────────
// PricingApplicationPage - shared membership application page
// Routes: /venue-pricing-application · /vendor-pricing-application
//         /boutique-pricing-application
// One design, one vocabulary; segment config drives copy, tiers, imagery.
// Luxury membership voice - see segments.js. Global SiteFooter renders from
// main.jsx, so this page must NOT render its own footer.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import HomeNav from '../../components/nav/HomeNav';
import { SEGMENTS, FOUNDING_LINE, FAQ } from './segments.js';
import { submitApplication } from '../../services/applicationSubmit.js';

const INK = '#0b0a08', SURFACE = '#111009', SURFACE2 = '#161410';
const BORDER = 'rgba(255,255,255,0.08)', BORDER2 = 'rgba(255,255,255,0.14)';
const GOLD = '#d39a21', GOLD_DIM = 'rgba(211,154,33,0.10)', GOLD_BORDER = 'rgba(211,154,33,0.30)';
const CREAM = '#f2efea', WARM = 'rgba(242,239,234,0.72)', MUTED = 'rgba(242,239,234,0.5)';
const GD = "'Gilda Display', Georgia, serif", NU = "'Nunito', sans-serif";

export default function PricingApplicationPage({ segment = 'venue', onBack, footerNav }) {
  const cfg = SEGMENTS[segment] || SEGMENTS.venue;
  const applyRef = useRef(null);
  const scrollToApply = () => applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Helmet>
        <title>{cfg.eyebrow} | Luxury Wedding Directory</title>
        <meta name="description" content={cfg.sub} />
      </Helmet>

      <div style={{ background: INK, color: CREAM, minHeight: '100vh', fontFamily: NU }}>
        <HomeNav darkMode hasHero={!!cfg.heroImage} sticky={!cfg.heroImage} onToggleDark={() => {}} />

        {/* ── Hero ── */}
        {cfg.heroImage ? (
          <section style={{ position: 'relative', minHeight: '82vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden' }}>
            <img src={cfg.heroImage} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(11,10,8,0.55) 0%, rgba(11,10,8,0.45) 40%, rgba(11,10,8,0.9) 100%)' }} />
            <div style={{ position: 'relative', maxWidth: 760, padding: '0 24px' }}>
              <Eyebrow text={cfg.eyebrow} />
              <h1 style={{ fontFamily: GD, fontSize: 'clamp(34px, 5vw, 60px)', fontWeight: 400, lineHeight: 1.12, margin: '0 0 22px', color: CREAM }}>{cfg.h1}</h1>
              <p style={{ fontFamily: NU, fontSize: 'clamp(15px,1.6vw,18px)', fontWeight: 300, lineHeight: 1.7, color: WARM, maxWidth: 580, margin: '0 auto' }}>{cfg.sub}</p>
              <button onClick={scrollToApply} style={ctaSolid}>Apply to Join</button>
            </div>
          </section>
        ) : (
          <section style={{ padding: 'clamp(180px,20vw,260px) 24px clamp(80px,9vw,120px)', textAlign: 'center' }}>
            <div style={{ maxWidth: 760, margin: '0 auto' }}>
              <Eyebrow text={cfg.eyebrow} />
              <h1 style={{ fontFamily: GD, fontSize: 'clamp(34px, 5vw, 60px)', fontWeight: 400, lineHeight: 1.12, margin: '0 0 22px', color: CREAM }}>{cfg.h1}</h1>
              <p style={{ fontFamily: NU, fontSize: 'clamp(15px,1.6vw,18px)', fontWeight: 300, lineHeight: 1.7, color: WARM, maxWidth: 580, margin: '0 auto' }}>{cfg.sub}</p>
              <button onClick={scrollToApply} style={ctaSolid}>Apply to Join</button>
            </div>
          </section>
        )}

        {/* ── Signature Virtual Try-On (the moat; boutique only, directly under hero) ── */}
        {cfg.tech && <TechSection tech={cfg.tech} />}

        {/* ── Founding / dual-directory line ── */}
        <section style={{ borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, background: SURFACE, padding: 'clamp(40px,6vw,72px) 24px', textAlign: 'center' }}>
          <p style={{ fontFamily: GD, fontStyle: 'italic', fontSize: 'clamp(18px,2.2vw,26px)', fontWeight: 300, lineHeight: 1.5, color: CREAM, maxWidth: 860, margin: '0 auto' }}>{FOUNDING_LINE}</p>
        </section>

        {/* ── Why join (benefits; segment-specific, above pricing) ── */}
        {cfg.whyJoin && <WhyJoinSection wj={cfg.whyJoin} />}

        {/* ── The four levels of standing ── */}
        <section style={{ padding: 'clamp(64px,8vw,110px) clamp(20px,4vw,56px)', maxWidth: 1320, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(36px,4.5vw,56px)' }}>
            <Eyebrow text="Membership" />
            <h2 style={{ fontFamily: GD, fontSize: 'clamp(26px,3vw,38px)', fontWeight: 400, color: CREAM, margin: '0 0 20px' }}>Four levels of standing.</h2>
            {cfg.credibility && (
              <p style={{ fontFamily: NU, fontSize: 'clamp(13px,1.3vw,15px)', fontWeight: 300, lineHeight: 1.7, color: WARM, maxWidth: 660, margin: '0 auto' }}>{cfg.credibility}</p>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(264px, 1fr))', gap: 22, alignItems: 'stretch' }}>
            {cfg.tiers.map((t) => <TierCard key={t.name} t={t} onApply={scrollToApply} />)}
          </div>
        </section>

        {/* ── Application (directly after pricing for a ready buyer) ── */}
        <section ref={applyRef} id="apply" style={{ background: SURFACE, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: 'clamp(72px,9vw,130px) clamp(24px,6vw,80px)' }}>
          <div style={{ maxWidth: 680, margin: '0 auto' }}>
            <ApplicationForm cfg={cfg} />
          </div>
        </section>

        {/* ── Editorial image band (after application; venue/vendor only) ── */}
        {cfg.sectionImage && (
          <section style={{ position: 'relative', height: 'clamp(280px,40vw,460px)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <img src={cfg.sectionImage} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(11,10,8,0.55)' }} />
            <p style={{ position: 'relative', fontFamily: GD, fontStyle: 'italic', fontSize: 'clamp(20px,2.6vw,32px)', color: CREAM, maxWidth: 700, padding: '0 24px', margin: 0 }}>
              Couples are not comparing prices. They are choosing who they trust.
            </p>
          </section>
        )}

        {/* ── FAQ ── */}
        <section style={{ padding: 'clamp(56px,7vw,96px) 24px', maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <Eyebrow text="Questions" />
          </div>
          {FAQ.map((f) => (
            <div key={f.q} style={{ borderTop: `1px solid ${BORDER}`, padding: '22px 0' }}>
              <p style={{ fontFamily: GD, fontSize: 18, color: CREAM, margin: '0 0 8px' }}>{f.q}</p>
              <p style={{ fontFamily: NU, fontSize: 14, color: WARM, lineHeight: 1.7, margin: 0 }}>{f.a}</p>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

function Eyebrow({ text }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
      <span style={{ width: 30, height: 1, background: GOLD }} />
      <span style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD }}>{text}</span>
      <span style={{ width: 30, height: 1, background: GOLD }} />
    </div>
  );
}

function TechSection({ tech }) {
  return (
    <section style={{
      position: 'relative', overflow: 'hidden',
      borderBottom: `1px solid ${GOLD_BORDER}`,
      background: `radial-gradient(120% 140% at 50% 0%, rgba(211,154,33,0.14) 0%, rgba(211,154,33,0.04) 38%, ${INK} 72%)`,
      padding: 'clamp(64px,8vw,104px) 24px', textAlign: 'center',
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative' }}>
        <Eyebrow text={tech.eyebrow} />
        <h2 style={{ fontFamily: GD, fontSize: 'clamp(28px,3.6vw,46px)', fontWeight: 400, lineHeight: 1.14, color: CREAM, margin: '0 0 20px' }}>{tech.heading}</h2>
        <p style={{ fontFamily: NU, fontSize: 'clamp(15px,1.7vw,19px)', fontWeight: 300, lineHeight: 1.75, color: WARM, maxWidth: 600, margin: '0 auto' }}>{tech.line}</p>
      </div>
    </section>
  );
}

function WhyJoinSection({ wj }) {
  return (
    <section style={{ padding: 'clamp(64px,8vw,104px) clamp(20px,4vw,56px)', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 'clamp(36px,4.5vw,52px)' }}>
        <Eyebrow text="Membership" />
        <h2 style={{ fontFamily: GD, fontSize: 'clamp(26px,3vw,38px)', fontWeight: 400, color: CREAM, margin: 0 }}>{wj.title}</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(248px, 1fr))', gap: 18 }}>
        {wj.bullets.map((b) => (
          <div key={b} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 5, padding: '24px 22px' }}>
            <span style={{ color: GOLD, fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>&#10022;</span>
            <span style={{ fontFamily: NU, fontSize: 15, fontWeight: 300, lineHeight: 1.6, color: CREAM }}>{b}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function TierCard({ t, onApply }) {
  const invitation = t.price === 'By invitation';
  const emph = !!t.emphasis; // Showcase, the visual focal card
  const btn = emph
    ? { marginTop: 'auto', padding: '14px 26px', background: GOLD, color: '#0a0906', border: 'none', borderRadius: 3, fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer' }
    : { ...ctaOutline, marginTop: 'auto' };
  return (
    <div style={{
      background: emph ? '#221c15' : '#1c1813',
      border: `1px solid ${emph ? 'rgba(211,154,33,0.55)' : 'rgba(255,255,255,0.14)'}`,
      borderRadius: 5,
      padding: emph ? '38px 28px 32px' : '32px 26px 28px',
      display: 'flex', flexDirection: 'column',
      transform: emph ? 'scale(1.045)' : 'none',
      boxShadow: emph ? '0 24px 60px -22px rgba(211,154,33,0.4), inset 0 0 0 1px rgba(211,154,33,0.18)' : 'none',
      position: 'relative', zIndex: emph ? 2 : 1,
    }}>
      {t.badge && (
        <span style={{ alignSelf: 'flex-start', fontFamily: NU, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: emph ? '#0a0906' : GOLD, background: emph ? GOLD : GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, borderRadius: 3, padding: '4px 10px', marginBottom: 18 }}>{t.badge}</span>
      )}
      <div style={{ fontFamily: NU, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 9 }}>{t.stands}</div>
      <h3 style={{ fontFamily: GD, fontSize: emph ? 27 : 24, fontWeight: 400, color: CREAM, margin: '0 0 4px' }}>{t.name}</h3>
      <p style={{ fontFamily: NU, fontSize: 12.5, color: MUTED, margin: '0 0 20px' }}>{t.tagline}</p>
      <div style={{ marginBottom: 22 }}>
        {invitation
          ? <span style={{ fontFamily: GD, fontStyle: 'italic', fontSize: 22, color: CREAM }}>By invitation</span>
          : <><span style={{ fontFamily: GD, fontSize: emph ? 34 : 30, color: CREAM }}>{t.price}</span><span style={{ fontFamily: NU, fontSize: 12, color: MUTED }}> {t.period}</span></>}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 }}>
        {t.benefits.map((b) => (
          <li key={b} style={{ display: 'flex', gap: 10, fontFamily: NU, fontSize: 13, color: WARM, lineHeight: 1.55, marginBottom: 11 }}>
            <span style={{ color: GOLD, flexShrink: 0 }}>&#10022;</span><span>{b}</span>
          </li>
        ))}
      </ul>
      <button onClick={onApply} style={btn}>{invitation ? 'Request an Invitation' : 'Apply to Join'}</button>
    </div>
  );
}

function ApplicationForm({ cfg }) {
  const [form, setForm] = useState({ name: '', businessName: '', email: '', phone: '', country: '', region: '', website: '', instagram: '', message: '', investmentReady: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.businessName) { setError('Please complete the required fields.'); return; }
    if (!form.phone || form.phone.replace(/\D/g, '').length < 6) { setError('Please add a phone number we can reach you on.'); return; }
    if (!form.investmentReady) { setError('Please confirm your investment readiness to proceed.'); return; }
    setError(''); setSubmitting(true);
    const res = await submitApplication({ form, sourcePage: cfg.sourcePage, leadSource: cfg.leadSource, category: cfg.category });
    setSubmitting(false);
    if (!res.ok) { setError('We could not submit your application. Please try again, or email us directly.'); return; }
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Eyebrow text="Received" />
        <h2 style={{ fontFamily: GD, fontSize: 'clamp(26px,3vw,38px)', fontWeight: 400, color: CREAM, margin: '0 0 16px' }}>Thank you. Your application is with our editors.</h2>
        <p style={{ fontFamily: NU, fontSize: 15, color: WARM, lineHeight: 1.7, maxWidth: 460, margin: '0 auto' }}>Every application is reviewed personally. We accept a limited number of partners, and we will be in touch within three business days.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <div style={{ textAlign: 'center', marginBottom: 'clamp(32px,4vw,52px)' }}>
        <Eyebrow text="Apply to Join" />
        <h2 style={{ fontFamily: GD, fontSize: 'clamp(26px,3vw,38px)', fontWeight: 400, color: CREAM, margin: '0 0 12px' }}>Introduce yourself.</h2>
        <p style={{ fontFamily: NU, fontSize: 14, color: WARM, lineHeight: 1.7, maxWidth: 480, margin: '0 auto' }}>Membership is by application. Tell us about your work, and our editorial team will be in touch.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 16 }}>
        <Field label="Your name *" value={form.name} onChange={(v) => set('name', v)} />
        <Field label="Business name *" value={form.businessName} onChange={(v) => set('businessName', v)} />
        <Field label="Email *" type="email" value={form.email} onChange={(v) => set('email', v)} />
        <Field label="Phone *" value={form.phone} onChange={(v) => set('phone', v)} />
        <Field label="Country" value={form.country} onChange={(v) => set('country', v)} />
        <Field label="Region" value={form.region} onChange={(v) => set('region', v)} />
        <Field label="Website" value={form.website} onChange={(v) => set('website', v)} />
        <Field label="Instagram" value={form.instagram} onChange={(v) => set('instagram', v)} />
      </div>
      <div style={{ marginTop: 16 }}>
        <Field label="A note about your work" value={form.message} onChange={(v) => set('message', v)} textarea />
      </div>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '20px 0 24px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.investmentReady} onChange={(e) => set('investmentReady', e.target.checked)} style={{ marginTop: 3, accentColor: GOLD }} />
        <span style={{ fontFamily: NU, fontSize: 13, color: WARM, lineHeight: 1.5 }}>I am ready to invest in membership of the Collection.</span>
      </label>
      {error && <p style={{ fontFamily: NU, fontSize: 13, color: '#e7a3a3', marginBottom: 16 }}>{error}</p>}
      <button type="submit" disabled={submitting} style={{ ...ctaSolid, margin: 0, width: '100%', opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Submitting…' : 'Submit Application'}
      </button>
    </form>
  );
}

function Field({ label, value, onChange, type = 'text', textarea }) {
  const base = { width: '100%', background: INK, border: `1px solid ${BORDER}`, borderRadius: 3, color: CREAM, fontFamily: NU, fontSize: 14, padding: '12px 14px', outline: 'none', boxSizing: 'border-box' };
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontFamily: NU, fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>{label}</span>
      {textarea
        ? <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} style={{ ...base, resize: 'vertical' }} onFocus={(e) => (e.target.style.borderColor = GOLD_BORDER)} onBlur={(e) => (e.target.style.borderColor = BORDER)} />
        : <input type={type} value={value} onChange={(e) => onChange(e.target.value)} style={base} onFocus={(e) => (e.target.style.borderColor = GOLD_BORDER)} onBlur={(e) => (e.target.style.borderColor = BORDER)} />}
    </label>
  );
}

const ctaSolid = { display: 'inline-block', marginTop: 32, padding: '15px 38px', background: GOLD, color: '#0a0906', border: 'none', borderRadius: 3, fontFamily: NU, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer' };
const ctaOutline = { padding: '13px 26px', background: 'transparent', color: GOLD, border: `1px solid ${GOLD_BORDER}`, borderRadius: 3, fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer' };
